import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import logger from 'src/util/logger';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_property_user.admin_property_user_list.table.pagination_error.message',
    defaultMessage: 'Failed to load more property users. Please try again.',
  },
});

const AdminPropertyUserListFragment = graphql`
  fragment AdminPropertyUserList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminPropertyUserListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    id
    propertyUsers(
      first: $count
      after: $cursor
    ) @connection(key: "AdminPropertyUserList_propertyUsers") {
      edges {
        cursor
        node {
          uuid
          externalIdentifier
          active {
            start
            finish
          }
          property {
            uuid
            title
            active { start finish }
          }
          user {
            uuid
            email
            givenName
            familyName
            active { start finish }
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={5} rowCount={3} />;
}

/**
 * Admin list of property users with load-more pagination. Field errors throw
 * to the route's PlaceholderErrorBoundary; load-more failures show an inline
 * retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactElement} AdminPropertyUserList component.
 */
function AdminPropertyUserList({ viewer: viewerRef = null }) {
  const {
    data: viewer, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminPropertyUserListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  const intl = useIntl();

  if (!viewer) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminPropertyUserList: the route query returned no viewer');
  }

  const { propertyUsers } = viewer;

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.page.title', defaultMessage: 'Enosi Admin - Property Users' },
  );
  const breadcrumbAdmin = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.breadcrumbs.admin.label', defaultMessage: 'Admin' },
  );
  const breadcrumbPropertyUsers = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.breadcrumbs.property_users.label', defaultMessage: 'Property Users' },
  );

  // Table headers
  const tableStatusHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.status.header', defaultMessage: 'Status' },
  );
  const tableIdHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.id.header', defaultMessage: 'ID' },
  );
  const tableExternalIdentifierTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.external_identifier.title', defaultMessage: 'External identifier' },
  );
  const tableExternalIdentifierLabel = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.external_identifier.label', defaultMessage: 'Ext ID' },
  );
  const tableUserHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.user.header', defaultMessage: 'User' },
  );
  const tablePropertyHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.property.header', defaultMessage: 'Property' },
  );

  // Action buttons
  const addPropertyUserButton = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.action.add_property_user.label', defaultMessage: 'Add Property User' },
  );

  // Load more button text
  const copyLoadMore = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' },
  );
  const copyNoMore = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.load_more_button.state_no_more.label', defaultMessage: 'No more property users to load' },
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPropertyUsers },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbPropertyUsers}</h1>
        <div className="float-end mb-4">
          <ButtonToolbar>
            <ButtonGroup className="ms-2">
              <Link
                to="/admin/property-users/new"
                className="btn btn-darken"
              >
                {addPropertyUserButton}
              </Link>
            </ButtonGroup>
          </ButtonToolbar>
        </div>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{tableStatusHeader}</th>
                <th scope="col">
                  <abbr title={tableIdHeader}>ID</abbr>
                </th>
                <th scope="col">
                  <abbr title={tableExternalIdentifierTitle}>{tableExternalIdentifierLabel}</abbr>
                </th>
                <th scope="col">{tableUserHeader}</th>
                <th scope="col">{tablePropertyHeader}</th>
              </tr>
            </thead>
            <tbody>
              {
                // properties && <pre>{JSON.stringify(properties, null, 2)}</pre>
                propertyUsers && propertyUsers.edges && propertyUsers.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    active, uuid, externalIdentifier, user, property,
                  } = node;
                  const {
                    uuid: userId, active: userActive, email: userEmail,
                  } = user;
                  const {
                    uuid: propertyId, title: propertyTitle, active: propertyActive,
                  } = property;

                  return (
                    <tr key={`admin-property-user-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={2}>
                        <UUID uuid={uuid} url={`/admin/property-users/${uuid}`} />
                        <br />
                        {`(${externalIdentifier})`}
                      </th>
                      <td>
                        <Link to={`/admin/users/${userId}`}>
                          {displayUserName(intl, user)}
                        </Link>
                        <br />
                        {userEmail}
                        <br />
                        <BadgeActive
                          start={userActive.start}
                          finish={userActive.finish}
                        />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${propertyId}`}>
                          {propertyTitle}
                        </Link>
                        <br />
                        <UUID uuid={propertyId} />
                        <br />
                        <BadgeActive
                          start={propertyActive.start}
                          finish={propertyActive.finish}
                        />
                      </td>
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            hasNext={hasNext}
            onClick={loadMore}
          />
        </div>
      </div>
    </>
  );
}

AdminPropertyUserList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminPropertyUserList;
